import React from "react";

import { cn } from "../../../utils/cn";

import { columnClassName, useTableConfig } from "./Table";

export interface TableRowProps {
    /**
     * The row's cells, keyed by column `key`. Each cell is placed + sized +
     * aligned by its column definition + the shared grid template (read from
     * the `Table` context), so a row never re-declares widths — it aligns to
     * the same tracks as the header. Missing keys render an empty cell.
     */
    cells: Record<string, React.ReactNode>;
    /**
     * When present the row becomes a clickable `<button>` with the hover
     * treatment; otherwise it's a plain non-interactive row.
     */
    onClick?: () => void;
    /** Accessible label for the clickable row. */
    ariaLabel?: string;
    /** Sim-only cursor target key, so `useSimCursor().moveTo(key)` can travel
     *  to this row. Inert in production. */
    "data-sim-cursor-key"?: string;
    /** Sim-only — force the clickable row's HOVER visual (bg-surface-sunken)
     *  with no pointer present, for scripted cursor tours. CSS `hover:` can't
     *  be JS-driven; an unconditional bg utility trails the `hover:` one so
     *  tailwind-merge resolves in its favor. Default `false`. */
    forceHover?: boolean;
}

/**
 * TableRow — one row of a `Table`. Reads the enclosing `Table`'s `columns` +
 * responsive grid template from context and lays out its `cells` record on
 * that grid, so every cell aligns to the same track as the header and hidden
 * columns drop out in lockstep. With `onClick` it renders as the clickable
 * `<button>` (hover:bg-surface-sunken); without, a plain row.
 *
 * Generic Component: cells are opaque `ReactNode`s and the config comes from
 * the `Table` context — it knows no Solomon/YaaS entity, so it never imports a
 * Domain Component (dependency firewall).
 */
export function TableRow({
    cells,
    onClick,
    ariaLabel,
    "data-sim-cursor-key": simCursorKey,
    forceHover = false
}: TableRowProps): React.ReactElement {
    const { columns, gridClassName } = useTableConfig();

    const content = columns.map((column) => (
        <div
            key={column.key}
            className={cn("min-w-0", columnClassName(column))}
        >
            {cells[column.key] ?? null}
        </div>
    ));

    if (onClick) {
        return (
            <button
                type="button"
                onClick={onClick}
                aria-label={ariaLabel}
                data-sim-cursor-key={simCursorKey}
                className={cn(
                    "w-full text-left px-4 py-4 grid hover:bg-surface-sunken transition-colors duration-150",
                    gridClassName,
                    // Forced hover trails `hover:` so tailwind-merge wins.
                    forceHover && "bg-surface-sunken"
                )}
            >
                {content}
            </button>
        );
    }

    return (
        <div className={cn("w-full px-4 py-4 grid", gridClassName)}>
            {content}
        </div>
    );
}
